import React from 'react';
import capitalize from 'lodash/capitalize';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import CalendarIcon from '@liquidweb-dev/undercurrent/icons/lucide/Calendar';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import OSIcon from 'components/OSIcon';
import DisplayTypeIcon from 'components/DisplayTypeIcon';

export interface KeyItemProps {
	label?: string;
	icon?: React.ReactNode;
	value?: string;
}

const KeyItem = ({ label, icon, value }: KeyItemProps) => {
	if (!label || !icon || !value) return null;

	return (
		<Stack
			direction="column"
			sx={{
				gap: 50,
				minWidth: 0,
				flex: 1,
				alignItems: 'flex-start',
			}}
		>
			<Typography
				variant="label-md"
				sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
			>
				{label}
			</Typography>
			<Stack
				direction="row"
				sx={{
					minHeight: '24px',
					paddingY: 50,
					paddingX: 0,
					justifyContent: 'center',
					alignItems: 'center',
					gap: 50,
				}}
			>
				{icon}
				<Typography variant="label-md">{value}</Typography>
			</Stack>
		</Stack>
	);
};

interface AssetOverviewProps {
	data?: AssetDetails;
}

const AssetOverview = ({ data }: AssetOverviewProps) => {
	const { domain, ip, cycle, displayType, osFormatted } = data || {};

	return (
		<Stack direction="column" sx={{ gap: 200, alignItems: 'stretch' }}>
			<Stack
				direction="row"
				sx={{
					justifyContent: 'space-between',
				}}
			>
				<TypographyOverflow
					variant="title-xs"
					sx={{
						fontWeight: 500,
					}}
				>
					{domain}
				</TypographyOverflow>
				<Typography
					variant="body-sm"
					sx={[
						{
							fontWeight: 400,
						},
						(theme) => ({ color: theme.vars.palette.uc.text.weak }),
					]}
				>
					{ip}
				</Typography>
			</Stack>
			<Stack
				direction="row"
				sx={{
					width: '100%',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<KeyItem
					label="Billing cycle"
					icon={
						<CalendarIcon
							sx={({ size }) => ({ height: size.xS, width: size.xS })}
						/>
					}
					value={capitalize(cycle)}
				/>
				<KeyItem
					label="Type"
					icon={
						<DisplayTypeIcon
							displayType={displayType || ''}
							sx={({ size }) => ({
								height: size.xS,
								width: size.xS,
								flexShrink: 0,
							})}
						/>
					}
					value={displayType}
				/>
				<KeyItem
					label="OS"
					icon={
						<OSIcon
							os={osFormatted || ''}
							sx={{
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
							}}
						/>
					}
					value={osFormatted}
				/>
			</Stack>
		</Stack>
	);
};

export default AssetOverview;
